import { useMemo, useRef } from "react";
import { useInView } from "../../RoyalMahal/hooks";
import { between, createRng, r2, round } from "./shapes";

/*
 * Kolams for South Indian invites: rice-flour patterns drawn around a grid of dots (pulli),
 * in white over a red-ochre (kaavi) border, as on a doorstep on a wedding morning.
 * They stand in for the Holi colour bursts (art/ColorBlast.js) behind the closing message.
 * When the page comes into view each kolam is drawn: its dots are set down, then its lines
 * trace around them. It rests a while, fades, and is drawn again, each on its own rhythm.
 * The lines are single continuous curves, as a kolam is drawn without lifting the hand:
 * rose curves for the petals, and epicycles for the rings of loops that go round each dot.
 */

const TAU = Math.PI * 2;
const polar = (r, a) => [r * Math.cos(a), r * Math.sin(a)];

/** A closed polyline through `steps` samples of `f` on [0, 2π), with its length (for drawing it in). */
const closedCurve = (f, steps) => {
  const points = Array.from({ length: steps }, (_, i) => f((TAU * i) / steps));
  let length = 0;
  points.forEach(([x, y], i) => {
    const [nx, ny] = points[(i + 1) % steps];
    length += Math.hypot(nx - x, ny - y);
  });
  return { d: `M${points.map(([x, y]) => `${r2(x)},${r2(y)}`).join("L")}Z`, length: round(length) };
};

/** `2k` petals of radius `R` in one line (r = R cos kθ, k even), with a dot inside each petal. */
const petals = (R, k, rot = 0) => ({
  ...closedCurve((t) => polar(R * Math.cos(k * t), t + rot), 90 * k),
  dots: Array.from({ length: 2 * k }, (_, j) => polar(R * 0.55, rot + (j * Math.PI) / k)),
});

/**
 * A ring of |q - 1| loops in one line: the epicycle A·e^{it} + B·e^{iqt}. With q < 0 the loops
 * turn outward, with q > 0 inward. A dot sits in the middle of each loop: between its tip and
 * the point where the line crosses itself.
 */
const loopRing = (A, B, q, rot = 0) => {
  const side = q < 0 ? 1 : -1;
  // Along a loop's axis: how far off the axis the line is, and how far out from the centre
  const across = (d) => A * Math.sin(d) + side * B * Math.sin(q * d);
  const along = (d) => A * Math.cos(d) + side * B * Math.cos(q * d);
  let lo = 1e-4;
  let hi = lo;
  while (across(hi) * across(lo) > 0 && hi < 1) hi += 0.002;
  for (let i = 0; i < 40; i += 1) {
    const mid = (lo + hi) / 2;
    if (across(mid) * across(lo) > 0) lo = mid;
    else hi = mid;
  }
  const centre = (along(0) + along(lo)) / 2;
  const offset = side > 0 ? 0 : Math.PI / (q - 1);
  return {
    ...closedCurve((t) => [A * Math.cos(t + rot) + B * Math.cos(q * t + rot), A * Math.sin(t + rot) + B * Math.sin(q * t + rot)], 600),
    dots: Array.from({ length: Math.abs(q - 1) }, (_, j) => polar(centre, (TAU * j) / (q - 1) + offset + rot)),
  };
};

const ring = (r, n, rot = 0) => Array.from({ length: n }, (_, j) => polar(r, rot + (TAU * j) / n));

// Four kolams, each drawn in a 100-unit box around its centre
const DESIGNS = {
  // Lotus: eight petals inside a ring of eight outward loops
  padma: () => {
    const inner = petals(21, 4);
    const outer = loopRing(34, 10, -7);
    return { lines: [inner, outer], dots: [[0, 0], ...inner.dots, ...outer.dots, ...ring(30, 8, Math.PI / 8)] };
  },
  // Wheel: four petals inside a ring of interlaced inward loops
  chakra: () => {
    const inner = petals(13, 2, Math.PI / 4);
    const outer = loopRing(31, 12, 9);
    return { lines: [inner, outer], dots: [[0, 0], ...inner.dots, ...outer.dots, ...ring(47, 8)] };
  },
  // Flower: twelve petals inside a ring of twelve small loops
  malar: () => {
    const inner = petals(25, 6);
    const outer = loopRing(37, 8, -11);
    return { lines: [inner, outer], dots: [[0, 0], ...inner.dots, ...outer.dots] };
  },
  // Small: two four-petal flowers, one turned over the other
  chinna: () => {
    const a = petals(30, 2);
    const b = petals(30, 2, Math.PI / 4);
    return { lines: [a, b], dots: [[0, 0], ...a.dots, ...b.dots] };
  },
};

// Kaavi red ochre, rani pink, leaf green, marigold
const BORDERS = ["#b5442b", "#c2185b", "#2e6b2a", "#d9800b"];

/*
 * Where the kolams sit (percent of the page) and how big (a share of --kolam, set in CSS).
 * They keep to the sides, clear of the message; the ones marked `wide` only show where the
 * page is wide enough to have room beside it.
 */
const SPOTS = [
  { left: 5, top: 10, scale: 1, design: "padma" },
  { left: 95, top: 16, scale: 0.9, design: "chakra" },
  { left: 3, top: 47, scale: 0.8, design: "malar" },
  { left: 97, top: 50, scale: 0.75, design: "chinna" },
  { left: 21, top: 30, scale: 0.7, design: "chinna", wide: true },
  { left: 79, top: 34, scale: 0.85, design: "malar", wide: true },
];

const Kolam = ({ seed = 23, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.15 });

  const kolams = useMemo(() => {
    const rng = createRng(seed);
    return SPOTS.map((spot, i) => ({
      ...spot,
      ...DESIGNS[spot.design](),
      border: BORDERS[i % BORDERS.length],
      rotate: round(between(rng, -12, 12)),
      delay: round(i * 0.35 + between(rng, 0, 0.3)),
      duration: round(between(rng, 11, 15)),
    }));
  }, [seed]);

  return (
    <div ref={ref} className={`ut-kolams ${inView ? "is-in" : ""} ${className}`} aria-hidden="true">
      {kolams.map((k, i) => (
        <svg
          key={i}
          className={`ut-kolam ${k.wide ? "ut-kolam--wide" : ""}`}
          viewBox="-52 -52 104 104"
          style={{
            left: `${k.left}%`,
            top: `${k.top}%`,
            "--scale": k.scale,
            "--rotate": `${k.rotate}deg`,
            "--border": k.border,
            "--delay": `${k.delay}s`,
            "--duration": `${k.duration}s`,
          }}
        >
          {/* The kaavi border under every line, then the white rice flour over it */}
          {["ut-kolam__border", "ut-kolam__flour"].map((layer) =>
            k.lines.map((line, j) => (
              <path
                key={`${layer}-${j}`}
                className={`ut-kolam__line ${layer}`}
                d={line.d}
                style={{ "--len": line.length, "--line-delay": `${round(j * 0.9)}s` }}
              />
            )),
          )}
          {k.dots.map(([x, y], j) => (
            <circle key={j} className="ut-kolam__dot" cx={r2(x)} cy={r2(y)} r="2" style={{ "--dot-delay": `${round(j * 0.03)}s` }} />
          ))}
        </svg>
      ))}
    </div>
  );
};

export default Kolam;
